import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useThemeStore } from '@/store/theme'
import { toAbsoluteUrl } from '@/utils/seo'

/**
 * Per-route document head.
 *
 * Written imperatively rather than with a head library (or React 19's
 * `<title>`/`<meta>` hoisting) because the hoisting path does not dedupe
 * against the static fallback tags already in `index.html` — crawlers that
 * don't run JS need those, and scrapers that do must not then see two
 * `meta[name=description]`.
 *
 * Every managed tag is rewritten on every call, falling back to the tenant
 * defaults when a page supplies nothing. That makes the head a pure function of
 * the current route, so stale values can't survive a navigation and no
 * unmount-restore bookkeeping is needed.
 */

const JSON_LD_ELEMENT_ID = 'seo-json-ld'

/**
 * The tags shipped in index.html, captured before anything overwrites them.
 * These are the no-JS fallbacks, and double as this hook's defaults.
 */
const STATIC_DEFAULTS = {
  description: readInitialContent('meta[name="description"]') ?? '',
  image: readInitialContent('meta[property="og:image"]') ?? '/og-image.jpg',
}

function readInitialContent(selector: string): string | null {
  if (typeof document === 'undefined') return null
  return document.querySelector(selector)?.getAttribute('content') ?? null
}

function upsertMeta(attribute: 'name' | 'property', key: string, content: string): void {
  const selector = `meta[${attribute}="${key}"]`
  let element = document.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

function removeMeta(attribute: 'name' | 'property', key: string): void {
  document.querySelector(`meta[${attribute}="${key}"]`)?.remove()
}

function upsertLink(rel: string, href: string): void {
  let element = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!element) {
    element = document.createElement('link')
    element.rel = rel
    document.head.appendChild(element)
  }
  element.href = href
}

function upsertJsonLd(data: object | null | undefined): void {
  const existing = document.getElementById(JSON_LD_ELEMENT_ID)
  if (!data) {
    existing?.remove()
    return
  }

  const element = existing ?? document.createElement('script')
  if (!existing) {
    element.id = JSON_LD_ELEMENT_ID
    element.setAttribute('type', 'application/ld+json')
    document.head.appendChild(element)
  }
  element.textContent = JSON.stringify(data)
}

export interface SeoOptions {
  /** Page title, without the store-name suffix. Omit to show the store name alone. */
  title?: string | null
  /** Plain text, ideally <= 160 chars. Run CMS HTML through `toMetaDescription` first. */
  description?: string | null
  /** Share image. Relative paths are resolved against the current origin. */
  image?: string | null
  /** Canonical path. Defaults to the current pathname (query and hash dropped). */
  canonicalPath?: string
  /** og:type — `website` for listings, `article` for news/events, `product` for PDPs. */
  type?: 'website' | 'article' | 'product'
  /** Keeps a page out of the index: cart, checkout, account, search. */
  noIndex?: boolean
  /** Structured data object, serialized into a single application/ld+json script. */
  jsonLd?: object | null
}

export function useSeo({
  title,
  description,
  image,
  canonicalPath,
  type = 'website',
  noIndex = false,
  jsonLd,
}: SeoOptions): void {
  const { pathname } = useLocation()
  const tenantTheme = useThemeStore((store) => store.tenantTheme)

  const siteName = tenantTheme.name
  const logoUrl = tenantTheme.logoUrl
  const serializedJsonLd = jsonLd ? JSON.stringify(jsonLd) : null

  useEffect(() => {
    const resolvedTitle = title ? `${title} | ${siteName}` : siteName
    const resolvedDescription = description || STATIC_DEFAULTS.description
    // Facebook, X and most other scrapers reject SVG share images, and tenant
    // logos are often .svg — fall back past those to the raster default.
    const rasterLogo = logoUrl && !/\.svgx?(\?|$)/i.test(logoUrl) ? logoUrl : undefined
    const resolvedImage = toAbsoluteUrl(image || rasterLogo || STATIC_DEFAULTS.image)
    const resolvedUrl = toAbsoluteUrl(canonicalPath ?? pathname)

    document.title = resolvedTitle

    upsertMeta('name', 'description', resolvedDescription)

    upsertMeta('property', 'og:title', resolvedTitle)
    upsertMeta('property', 'og:description', resolvedDescription)
    upsertMeta('property', 'og:type', type)
    upsertMeta('property', 'og:url', resolvedUrl)
    upsertMeta('property', 'og:image', resolvedImage)
    upsertMeta('property', 'og:site_name', siteName)

    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', resolvedTitle)
    upsertMeta('name', 'twitter:description', resolvedDescription)
    upsertMeta('name', 'twitter:image', resolvedImage)

    upsertLink('canonical', resolvedUrl)

    if (noIndex) {
      upsertMeta('name', 'robots', 'noindex, nofollow')
    } else {
      removeMeta('name', 'robots')
    }

    upsertJsonLd(serializedJsonLd ? (JSON.parse(serializedJsonLd) as object) : null)
  }, [
    title,
    description,
    image,
    canonicalPath,
    pathname,
    type,
    noIndex,
    siteName,
    logoUrl,
    serializedJsonLd,
  ])
}

export default useSeo
